import { Box, Typography } from '@mui/material';
import { getTeamImage, getTeamText } from '../../../models/TeamConstants';
import {
  RADIUS,
  CIRCUMFERENCE,
  getRingColor,
  getResultHeading,
  getEarnedTitle,
  STAR_POSITIONS,
} from '../utils/quizResultsHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ResultsCardProps {
  score: number;
  totalQuestions: number;
  modeName: string;
  team: string | null;
}

// ─── ScoreRing ────────────────────────────────────────────────────────────────

function ScoreRing({ score, total }: { score: number; total: number }) {
  const pct = total > 0 ? score / total : 0;
  const offset = CIRCUMFERENCE * (1 - pct);
  const color = getRingColor(pct);

  return (
    <Box position="relative" width={128} height={128}>
      <svg
        width="128" height="128" viewBox="0 0 128 128"
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx="64" cy="64" r={RADIUS}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10"
        />
        <circle
          cx="64" cy="64" r={RADIUS}
          fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={CIRCUMFERENCE} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease, stroke 0.4s ease' }}
        />
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: '2rem', fontWeight: 500, lineHeight: 1, color: '#e0c9f0' }}>
          {score}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(224,201,240,0.55)', mt: '2px' }}>
          of {total}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── StatCell ─────────────────────────────────────────────────────────────────

function StatCell({ value, label, color }: { value: string | number; label: string; color: string }) {
  return (
    <Box flex={1} display="flex" flexDirection="column" alignItems="center" sx={{ py: 1.5, px: 1 }}>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 500, lineHeight: 1, color, mb: '4px' }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.09em', color: 'rgba(224,201,240,0.45)' }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── ResultsCard ─────────────────────────────────────────────────────────────

export default function ResultsCard({ score, totalQuestions, modeName, team }: ResultsCardProps) {
  const pct = totalQuestions > 0 ? score / totalQuestions : 0;
  const missed = totalQuestions - score;
  const earnedTitle = getEarnedTitle(score, totalQuestions, modeName);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '0.5px solid rgba(127,119,221,0.3)',
        backgroundColor: '#0d0820',
      }}
    >
      {/* Header */}
      <Box sx={{
        background: '#13093a',
        px: 3, pt: 3, pb: 2,
        textAlign: 'center',
        borderBottom: '0.5px solid rgba(127,119,221,0.15)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {STAR_POSITIONS.map((star, i) => (
          <Box key={i} sx={{
            position: 'absolute',
            width: `${star.size}px`, height: `${star.size}px`,
            borderRadius: '50%',
            backgroundColor: 'rgba(224,201,240,0.35)',
            top: star.top, left: star.left,
          }} />
        ))}
        <Typography sx={{ fontSize: '0.65rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9f86c0', mb: 0.5 }}>
          The Quizzler · Wizarding World Trivia
        </Typography>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#e0c9f0', mb: 0.25 }}>
          {getResultHeading(pct)}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(159,134,192,0.7)' }}>
          {modeName} mode
        </Typography>
      </Box>

      {/* Score ring + title badge */}
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ py: 2.5, px: 3 }} gap={1.25}>
        <ScoreRing score={score} total={totalQuestions} />
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: 'rgba(83,74,183,0.2)',
          border: '0.5px solid rgba(83,74,183,0.5)',
          borderRadius: '20px', px: 2, py: '5px',
        }}>
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M7 1L8.5 5H13L9.5 7.5L10.5 12L7 9.5L3.5 12L4.5 7.5L1 5H5.5L7 1Z" fill="#AFA9EC"/>
          </svg>
          <Typography sx={{ fontSize: '0.8rem', color: '#AFA9EC', fontWeight: 500 }}>
            {earnedTitle}
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ height: '0.5px', background: 'rgba(127,119,221,0.15)' }} />

      {/* Stats row */}
      <Box display="flex" sx={{
        borderBottom: '0.5px solid rgba(127,119,221,0.15)',
        '& > *:not(:last-child)': { borderRight: '0.5px solid rgba(127,119,221,0.15)' },
      }}>
        <StatCell value={score}    label="Correct" color="#4caf82" />
        <StatCell value={missed}   label="Missed"  color="#E24B4A" />
        <StatCell value={modeName} label="Mode"    color="#AFA9EC" />
      </Box>

      {/* Team strip */}
      <Box display="flex" alignItems="center" gap={1.5} sx={{
        px: 2.5, py: 1.5,
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '0.5px solid rgba(127,119,221,0.15)',
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '6px',
          background: 'rgba(83,74,183,0.25)',
          border: '0.5px solid rgba(83,74,183,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.2rem', flexShrink: 0,
        }}>
          {getTeamImage(team)}
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#e0c9f0' }}>
            {team ?? 'No team'}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(159,134,192,0.65)', mt: '1px' }}>
            {team ? getTeamText(team) : 'Pick a team on your profile'}
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2.5, py: 1, textAlign: 'right' }}>
        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(159,134,192,0.4)', fontStyle: 'italic' }}>
          TheQuizzler.app
        </Typography>
      </Box>
    </Box>
  );
}